/**
 * AS-10775 — custom report column names.
 *
 * A merchant renames a report column in Manage Columns. `ColumnSelector` stores
 * the new text on the selected column's `name`, seeded from the server default
 * name, and emits it with the selection. There is no "was this renamed" flag
 * anywhere in the persisted shape, so the rule is: a name that is non-empty and
 * differs from the server default IS the merchant's custom label.
 *
 * Everything that needs to render or transmit a column label goes through here,
 * so the rule cannot drift between the table headers, the composition cells and
 * the export request.
 */

/** A column as the server describes it, or as the selection stores it. */
export interface LabelledColumn {
  code: string;
  name?: string;
}

/**
 * The label to display for one column: the merchant's custom name when they set
 * one, otherwise the server default. Never returns whitespace or `undefined`,
 * so a cleared name falls back rather than rendering a blank header.
 */
export function resolveColumnLabel(
  customName: string | undefined | null,
  defaultName: string | undefined | null,
): string {
  const custom = (customName || '').trim();
  if (custom) {
    return custom;
  }
  return (defaultName || '').trim();
}

/**
 * Map of `code -> custom label`, holding ONLY the columns the merchant actually
 * renamed. Columns left at their default are absent, so callers can treat a hit
 * as "there is an override here" without re-deriving the comparison.
 *
 * A code the definitions do not describe is treated as NOT renamed. Without a
 * default there is nothing to compare against, so calling it a rename would be
 * a guess — and guessing wrong writes a permanent override onto a column the
 * merchant never touched. It also makes the empty-definitions case safe: a save
 * fired before `report.columns` has loaded yields no labels rather than
 * freezing every current name into the saved record.
 */
export function buildCustomColumnLabels(
  selectedColumns: LabelledColumn[] | null | undefined,
  columnDefs: LabelledColumn[] | null | undefined,
): Map<string, string> {
  const labels = new Map<string, string>();
  if (!selectedColumns || !selectedColumns.length) {
    return labels;
  }

  const defaultNames = new Map<string, string>();
  for (const def of columnDefs || []) {
    if (def && def.code) {
      defaultNames.set(def.code, (def.name || '').trim());
    }
  }

  for (const column of selectedColumns) {
    if (!column || !column.code) {
      continue;
    }
    const custom = (column.name || '').trim();
    if (!custom) {
      continue;
    }
    if (!defaultNames.has(column.code)) {
      continue;
    }
    if (defaultNames.get(column.code) === custom) {
      continue;
    }
    labels.set(column.code, custom);
  }
  return labels;
}

/** One resolved standard-mode header: the server def plus the label to show. */
export interface ResolvedHeaderColumn {
  code: string;
  text: string;
  column: any;
}

/**
 * Standard view mode. `TableReport.buildStandardHeaders()` walks the selection
 * and, for each entry, finds the matching server definition and renders that
 * definition's `name` — which is where the rename was being lost. This returns
 * the same ordered list with the label already resolved.
 *
 * An empty selection means "show everything", matching the previous behaviour.
 */
export function resolveHeaderColumns(
  selectedColumns: LabelledColumn[] | null | undefined,
  reportColumns: any[] | null | undefined,
): ResolvedHeaderColumn[] {
  const defs = reportColumns || [];
  const selection =
    selectedColumns && selectedColumns.length ? selectedColumns : defs;

  const resolved: ResolvedHeaderColumn[] = [];
  for (const selected of selection) {
    if (!selected || !selected.code) {
      continue;
    }
    const def = defs.find((c: any) => c && c.code === selected.code);
    if (!def) {
      continue;
    }
    resolved.push({
      code: def.code,
      text: resolveColumnLabel(selected.name, def.name),
      column: def,
    });
  }
  return resolved;
}

/**
 * Composition view mode. `TableReport.buildCompositionHeaders()` builds a
 * `code -> server def` map that four separate helpers then read `.name` from.
 * Merging the label in here fixes all of them at once, rather than patching
 * each `text:` site — which is how two of them were missed in the first place.
 *
 * The merged def is a clone: `allColumnDefsMap` holds the shared objects from
 * `report.columns`, and renaming those in place would leak a per-selection
 * label into the metadata every other consumer reads.
 */
export function buildLabelledColumnDefs(
  selectedColumns: LabelledColumn[] | null | undefined,
  allColumnDefsMap: Map<string, any>,
): Map<string, any> {
  const defs = new Map<string, any>();
  for (const selected of selectedColumns || []) {
    if (!selected || !selected.code) {
      continue;
    }
    const def = allColumnDefsMap.get(selected.code);
    if (!def) {
      continue;
    }
    defs.set(selected.code, {
      ...def,
      name: resolveColumnLabel(selected.name, def.name),
      // Kept so orderings that must NOT shift when a merchant renames a column
      // (pivot constituent sorting) have the stable name to sort on.
      defaultName: def.name,
    });
  }
  return defs;
}

/**
 * Copy of a persisted column selection with an explicit `label` on the entries
 * the merchant renamed. Used when saving a scheduled report or a dashboard
 * card, whose exports are produced server-side long after the page is gone.
 *
 * The backend honours `label` and never infers one from `name`: records written
 * before AS-10775 carry server defaults that may since have changed, and
 * inferring would retroactively freeze those stale names into old schedules.
 */
export function attachCustomColumnLabels(
  selectedColumns: any[] | null | undefined,
  columnDefs: LabelledColumn[] | null | undefined,
): any[] {
  if (!selectedColumns || !selectedColumns.length) {
    return [];
  }
  const labels = buildCustomColumnLabels(selectedColumns, columnDefs);
  return selectedColumns.map((column: any) => {
    const label = column && column.code ? labels.get(column.code) : undefined;
    return label ? { ...column, label } : column;
  });
}
